import type { CanFrame } from '../canlog/types';

export type NormalizedLiveFrame = CanFrame & {
  bus?: string;
};

export type LiveFrameNormalizationResult = {
  frames: NormalizedLiveFrame[];
  rawFrameCount: number;
  parsedFrameCount: number;
  invalidFrameCount: number;
};

type RawFrame = Record<string, unknown>;
type LiveFrameDraft = Omit<NormalizedLiveFrame, 'index' | 'deltaMs'>;

function isLiveFrameDraft(value: LiveFrameDraft | null): value is LiveFrameDraft {
  return value !== null;
}

function parseCanId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/^can/i, '').replace(/^0x/i, '');
  const parsed = Number.parseInt(normalized, /^[0-9]+$/.test(normalized) ? 10 : 16);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseByte(value: string) {
  const parsed = Number.parseInt(value.replace(/^0x/i, ''), 16);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 255 ? parsed : null;
}

function parseData(value: unknown): number[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => typeof item === 'number' ? Math.trunc(item) : parseByte(String(item)))
      .filter((item): item is number => typeof item === 'number' && item >= 0 && item <= 255)
      .slice(0, 64);
  }

  if (typeof value !== 'string') return [];
  const compact = value.trim();
  if (!compact) return [];

  if (/^[0-9a-fA-F]+$/.test(compact) && compact.length % 2 === 0) {
    return compact.match(/.{1,2}/g)?.map(parseByte).filter((item): item is number => item !== null).slice(0, 64) ?? [];
  }

  return compact
    .split(/[\s,:[\]{}()#.-]+/)
    .filter(Boolean)
    .map(parseByte)
    .filter((item): item is number => item !== null)
    .slice(0, 64);
}

function toHex(value: number, width = 2) {
  return value.toString(16).toUpperCase().padStart(width, '0');
}

function canIdHex(canId: number) {
  return `0x${toHex(canId, canId > 0x7FF ? 8 : 3)}`;
}

function dataHex(data: number[]) {
  return data.map((byte) => toHex(byte)).join(' ');
}

function parseTimestamp(value: unknown, fallback: number) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1_000_000_000 ? value - fallback : value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace(/[()]/g, ''));
    if (Number.isFinite(parsed)) return parsed > 1_000_000 ? parsed - fallback : parsed;
  }
  return fallback;
}

function read(raw: RawFrame, keys: string[]) {
  for (const key of keys) {
    if (raw[key] !== undefined) return raw[key];
  }
  return undefined;
}

function frameFromObject(raw: RawFrame, fallbackTimestamp: number): LiveFrameDraft | null {
  const canId = parseCanId(read(raw, ['canId', 'can_id', 'id', 'idHex', 'identifier', 'arbId', 'arbitration_id']));
  if (canId === null) return null;

  const data = parseData(read(raw, ['data', 'bytes', 'payload', 'dataHex', 'data_hex']));
  const dlcValue = read(raw, ['dlc', 'len', 'length']);
  const parsedDlc = typeof dlcValue === 'number' ? Math.trunc(dlcValue) : Number.parseInt(String(dlcValue ?? data.length), 10);
  const timestampMs = parseTimestamp(read(raw, ['timestampMs', 'timestamp_ms', 'timeMs', 'time_ms', 'timestamp', 'time', 'ts']), fallbackTimestamp);

  return {
    timestampMs,
    canId,
    canIdHex: canIdHex(canId),
    extended: Boolean(read(raw, ['extended', 'ext', 'isExtended']) ?? canId > 0x7FF),
    dlc: Number.isFinite(parsedDlc) ? parsedDlc : data.length,
    data,
    dataHex: dataHex(data),
    bus: typeof read(raw, ['bus', 'channel', 'iface', 'interface']) === 'string' ? String(read(raw, ['bus', 'channel', 'iface', 'interface'])) : undefined
  };
}

function parseCandumpLine(line: string, fallbackTimestamp: number): LiveFrameDraft | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  const candump = trimmed.match(/^(?:\((?<time>[\d.]+)\)\s+)?(?:(?<bus>\S+)\s+)?(?<id>[0-9A-Fa-f]{3,8})#(?<data>[0-9A-Fa-f]*)/);
  if (candump?.groups) {
    const canId = parseCanId(candump.groups.id);
    if (canId === null) return null;
    const data = parseData(candump.groups.data);
    const timestampSeconds = candump.groups.time ? Number.parseFloat(candump.groups.time) : null;
    return {
      timestampMs: Number.isFinite(timestampSeconds) && timestampSeconds !== null ? timestampSeconds * 1000 : fallbackTimestamp,
      canId,
      canIdHex: canIdHex(canId),
      extended: canId > 0x7FF,
      dlc: data.length,
      data,
      dataHex: dataHex(data),
      bus: candump.groups.bus
    };
  }

  const csv = trimmed.split(/[,\s]+/).filter(Boolean);
  if (csv.length >= 3) {
    const maybeTimestamp = Number.parseFloat(csv[0]);
    const idTokenIndex = Number.isFinite(maybeTimestamp) ? 1 : 0;
    const canId = parseCanId(csv[idTokenIndex]);
    if (canId === null) return null;
    const data = parseData(csv.slice(idTokenIndex + 1).join(' '));
    return {
      timestampMs: Number.isFinite(maybeTimestamp) ? maybeTimestamp : fallbackTimestamp,
      canId,
      canIdHex: canIdHex(canId),
      extended: canId > 0x7FF,
      dlc: data.length,
      data,
      dataHex: dataHex(data)
    };
  }

  return null;
}

function extractFrameArray(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const object = payload as Record<string, unknown>;
    for (const key of ['frames', 'live', 'data', 'items', 'rows']) {
      if (Array.isArray(object[key])) return object[key];
    }
  }
  return [];
}

export function normalizeLiveFramesDetailed(payload: unknown, startIndex = 0, previousFrame?: CanFrame): LiveFrameNormalizationResult {
  const fallbackTimestamp = performance.now();
  const rawItems = typeof payload === 'string'
    ? payload.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
    : extractFrameArray(payload);
  const parsedDrafts = typeof payload === 'string'
    ? rawItems.map((line) => parseCandumpLine(String(line), fallbackTimestamp))
    : rawItems.map((item) => item && typeof item === 'object' ? frameFromObject(item as RawFrame, fallbackTimestamp) : null);
  const rawFrames = parsedDrafts.filter(isLiveFrameDraft);

  let lastTimestamp = previousFrame?.timestampMs ?? null;
  const frames = rawFrames.map((frame, offset) => {
    const deltaMs = lastTimestamp === null ? null : frame.timestampMs - lastTimestamp;
    lastTimestamp = frame.timestampMs;
    return {
      ...frame,
      index: startIndex + offset,
      deltaMs
    };
  });

  return {
    frames,
    rawFrameCount: rawItems.length,
    parsedFrameCount: frames.length,
    invalidFrameCount: Math.max(0, rawItems.length - frames.length)
  };
}

export function normalizeLiveFrames(payload: unknown, startIndex = 0, previousFrame?: CanFrame): NormalizedLiveFrame[] {
  return normalizeLiveFramesDetailed(payload, startIndex, previousFrame).frames;
}

export function makeDemoFrames(startIndex: number, count = 8): NormalizedLiveFrame[] {
  const ids = [0x7E8, 0x123, 0x321, 0x18FEF100, 0x025, 0x626];
  const now = performance.now();
  return Array.from({ length: count }, (_, offset) => {
    const canId = ids[(startIndex + offset) % ids.length];
    const data = canId === 0x7E8
      ? [0x04, 0x41, (startIndex + offset) % 2 === 0 ? 0x0C : 0x0D, 0x1A, 0xF8].slice(0, (startIndex + offset) % 2 === 0 ? 5 : 4)
      : Array.from({ length: 8 }, (_, byteIndex) => (startIndex + offset + byteIndex * 17) & 0xFF);
    return {
      index: startIndex + offset,
      timestampMs: now + offset * 18,
      canId,
      canIdHex: canIdHex(canId),
      extended: canId > 0x7FF,
      dlc: data.length,
      data,
      dataHex: dataHex(data),
      deltaMs: offset === 0 ? null : 18,
      bus: 'demo'
    };
  });
}
